import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, X } from "lucide-react";
import { inputCls, Field, CheckGroup, GoldButton, meetsMinimums, Honeypot, PrivacyNote } from "./fields";
import { useDraft } from "./useMember";
import { useSubmitApplication } from "./useSubmitApplication";

const EMPTY = {
  owner_name: "", boat_name: "", length_ft: "", cockpit_capacity: "", cockpit_layout: "",
  features: [], other_features: "", photo_data_url: "",
};

/** Downscale to max 1600px JPEG so uploads stay small and pass proxy limits. */
const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
    img.src = url;
  });

export const YachtForm = ({ options, headers, onCreated }) => {
  const [form, setForm, clear] = useDraft("cc_draft_yacht", EMPTY);
  const { busy, submit } = useSubmitApplication({ path: "yacht", headers, onCreated, clearDraft: clear });
  const fileRef = useRef(null);
  const [showErrors, setShowErrors] = useState(false);
  const [website, setWebsite] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const MINS = [[form.owner_name, 2], [form.boat_name, 1], [form.cockpit_layout, 5]];

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    if (file.size > 15 * 1024 * 1024) return toast.error("Image too large — max 15 MB.");
    try {
      const data = await compressImage(file);
      setForm((f) => ({ ...f, photo_data_url: data }));
    } catch {
      toast.error("Could not read that image.");
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!meetsMinimums(MINS)) { setShowErrors(true); toast.error("Please complete the highlighted fields."); return; }
    const length = Number(form.length_ft), cap = Number(form.cockpit_capacity);
    if (length < options.min_yacht_length_ft) return toast.error(`Yachts must be at least ${options.min_yacht_length_ft} feet.`);
    if (cap < options.min_cockpit_capacity) return toast.error(`Cockpit must hold at least ${options.min_cockpit_capacity} people.`);
    if (!form.photo_data_url) return toast.error("A transom photo (back of the yacht) is required.");
    submit({ ...form, website, length_ft: length, cockpit_capacity: cap }, form.boat_name);
  };

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5" data-testid="yacht-form">
      <p className="text-xs text-[#94A3B8] border border-[#D4AF37]/25 bg-[#070A0F] p-3 leading-relaxed" data-testid="yacht-requirements-note">
        Minimum {options.min_yacht_length_ft} ft with a cockpit for {options.min_cockpit_capacity}+ people. Larger cockpits are preferred —
        the cockpit is where the cooking contests and crew dinners are filmed. Your name and email stay private;
        the boat name, transom photo and feature list appear on the voting card.
      </p>
      <Field label="Applicant / owner / company name" hint="private" value={form.owner_name} minLength={2} showErrors={showErrors} testId="yacht-owner-name">
        <input required value={form.owner_name} onChange={set("owner_name")} placeholder="Owner or company name" className={inputCls} data-testid="yacht-owner-name" />
      </Field>
      <Field label="Boat name" hint="shown publicly" value={form.boat_name} minLength={1} showErrors={showErrors} testId="yacht-boat-name">
        <input required value={form.boat_name} onChange={set("boat_name")} placeholder="M/Y …" className={inputCls} data-testid="yacht-boat-name" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Yacht length (ft)" hint={`min ${options.min_yacht_length_ft}`}>
          <input required type="number" min={options.min_yacht_length_ft} max={600} value={form.length_ft} onChange={set("length_ft")}
            placeholder="e.g. 120" className={inputCls} data-testid="yacht-length" />
        </Field>
        <Field label="Cockpit capacity (people)" hint={`min ${options.min_cockpit_capacity}`}>
          <input required type="number" min={options.min_cockpit_capacity} max={500} value={form.cockpit_capacity} onChange={set("cockpit_capacity")}
            placeholder="e.g. 20" className={inputCls} data-testid="yacht-capacity" />
        </Field>
      </div>
      <Field label="Transom photo" hint="back of the yacht — shown publicly">
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="yacht-photo-input" />
        {form.photo_data_url ? (
          <div className="relative border border-[#D4AF37]/40" data-testid="yacht-photo-preview">
            <img src={form.photo_data_url} alt="Transom preview" className="w-full h-56 object-cover" />
            <button type="button" onClick={() => setForm((f) => ({ ...f, photo_data_url: "" }))}
              className="absolute top-2 right-2 h-8 w-8 flex items-center justify-center bg-[#070A0F]/80 border border-[#D4AF37]/40 text-[#E8C15A] hover:bg-[#D4AF37] hover:text-[#070A0F] transition-colors"
              aria-label="Remove photo" data-testid="yacht-photo-remove">
              <X size={14} />
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => fileRef.current?.click()}
            className="w-full h-36 border border-dashed border-[#D4AF37]/40 flex flex-col items-center justify-center gap-2 text-[#94A3B8] hover:border-[#D4AF37] hover:text-[#E8C15A] transition-colors"
            data-testid="yacht-photo-pick-btn">
            <Camera size={22} />
            <span className="text-[11px] tracking-[0.2em] uppercase">Upload transom photo</span>
          </button>
        )}
      </Field>
      <Field label="Cockpit size & layout" value={form.cockpit_layout} minLength={5} showErrors={showErrors} testId="yacht-cockpit-layout">
        <textarea required rows={3} value={form.cockpit_layout} onChange={set("cockpit_layout")}
          placeholder="Dimensions, seating, tables, shade, access to the swim platform…" className={inputCls} data-testid="yacht-cockpit-layout" />
      </Field>
      <CheckGroup label="Features for the show" hint="shown publicly" options={options.yacht_features}
        value={form.features} onChange={(v) => setForm((f) => ({ ...f, features: v }))} testPrefix="yacht-feature" />
      <Field label="Other yacht features suitable for the show" hint="optional">
        <textarea rows={3} value={form.other_features} onChange={set("other_features")}
          placeholder="Galley setup, outdoor grills, entertaining space, toys, anything that films well…" className={inputCls} data-testid="yacht-other-features" />
      </Field>
      <Honeypot value={website} onChange={(e) => setWebsite(e.target.value)} />
      <PrivacyNote>The boat name, transom photo, size and features appear on the public voting card; your name and email stay private.</PrivacyNote>
      <GoldButton type="submit" disabled={busy} data-testid="yacht-submit">{busy ? "SENDING…" : "SUBMIT YACHT APPLICATION"}</GoldButton>
    </form>
  );
};
